import { LIVENESS_PHASES } from "./LivenessChallengeMachine";
import { FACE_POSITION_LIMITS } from "./FacePose";

export const LIVENESS_LOG_PREFIX = "[活体检测]";

export const LIVENESS_PHASE_NAMES = Object.freeze({
  [LIVENESS_PHASES.POSITION]: "面部位置校验",
  [LIVENESS_PHASES.TURN_FIRST_SIDE]: "首次左右转头",
  [LIVENESS_PHASES.TURN_RETURN_CENTER]: "转头后回正",
  [LIVENESS_PHASES.TURN_OPPOSITE_SIDE]: "另一侧转头",
  [LIVENESS_PHASES.NOD_CENTER]: "上下动作前回正",
  [LIVENESS_PHASES.NOD_MOVE]: "低头",
  [LIVENESS_PHASES.NOD_RETURN_CENTER]: "低头后回正",
  [LIVENESS_PHASES.NOD_OPPOSITE]: "抬头",
  [LIVENESS_PHASES.NOD_FINAL_CENTER]: "抬头后回正",
  [LIVENESS_PHASES.COMPLETE]: "全部动作完成",
  [LIVENESS_PHASES.TIMED_OUT]: "检测超时",
});

const getPhaseName = (phase) =>
  LIVENESS_PHASE_NAMES[phase] || phase || "未知步骤";

const getPoseDetails = (snapshot = {}) => {
  const details = {};
  if (Number.isFinite(snapshot.yaw)) {
    details["左右角度 yaw"] = `${snapshot.yaw.toFixed(1)}°`;
  }
  if (Number.isFinite(snapshot.pitch)) {
    details["上下角度 pitch"] = `${snapshot.pitch.toFixed(1)}°`;
  }
  return details;
};

const toPercent = (value) =>
  Number.isFinite(value) ? `${(value * 100).toFixed(1)}%` : "无数据";

const getPositionDetails = (snapshot = {}) => ({
  不合格原因: snapshot.positionFailures?.join("；") || "未知",
  人脸中心X: toPercent(snapshot.bounds?.centerX),
  人脸中心X要求: `${toPercent(FACE_POSITION_LIMITS.minCenterX)}～${toPercent(FACE_POSITION_LIMITS.maxCenterX)}`,
  人脸中心Y: toPercent(snapshot.bounds?.centerY),
  人脸中心Y要求: `${toPercent(FACE_POSITION_LIMITS.minCenterY)}～${toPercent(FACE_POSITION_LIMITS.maxCenterY)}`,
  人脸宽度: toPercent(snapshot.bounds?.width),
  人脸宽度要求: `${toPercent(FACE_POSITION_LIMITS.minWidth)}～${toPercent(FACE_POSITION_LIMITS.maxWidth)}`,
  人脸高度: toPercent(snapshot.bounds?.height),
  人脸高度要求: `${toPercent(FACE_POSITION_LIMITS.minHeight)}～${toPercent(FACE_POSITION_LIMITS.maxHeight)}`,
});

/**
 * 中文控制台日志器。
 *
 * 只记录动作状态、角度和技术结果，不记录图像、视频内容、证件号、
 * snCode 或完整上传 URL。
 */
export class LivenessConsoleLogger {
  constructor(output = console) {
    this.output = output;
    this.lastPhase = null;
    this.lastFaceDetected = null;
    this.lastPositionValid = null;
  }

  write(level, message, details) {
    const method = this.output[level] || this.output.log;
    if (!method) return;
    if (details && Object.keys(details).length > 0) {
      method.call(this.output, `${LIVENESS_LOG_PREFIX} ${message}`, details);
      return;
    }
    method.call(this.output, `${LIVENESS_LOG_PREFIX} ${message}`);
  }

  info(message, details) {
    this.write("info", message, details);
  }

  warn(message, details) {
    this.write("warn", message, details);
  }

  error(message, details) {
    this.write("error", message, details);
  }

  reset(sessionId) {
    this.lastPhase = null;
    this.lastFaceDetected = null;
    this.lastPositionValid = null;
    this.info("新的检测会话已开始", { 会话序号: sessionId });
  }

  logSnapshot(snapshot = {}) {
    const faceDetected = Boolean(snapshot.faceDetected);
    const positionValid = Boolean(snapshot.positionValid);

    if (faceDetected !== this.lastFaceDetected) {
      if (faceDetected) {
        this.info("人脸检测结果：已检测到人脸");
      } else {
        this.warn("人脸检测结果：未检测到人脸");
      }
      this.lastFaceDetected = faceDetected;
    }

    if (!faceDetected) {
      this.lastPositionValid = null;
    } else if (positionValid !== this.lastPositionValid) {
      if (positionValid) {
        this.info("面部位置结果：位置合格", getPoseDetails(snapshot));
      } else {
        this.warn(
          "面部位置结果：请将完整面部移入引导框",
          getPositionDetails(snapshot),
        );
      }
      this.lastPositionValid = positionValid;
    }

    if (!snapshot.phase || snapshot.phase === this.lastPhase) return;

    if (this.lastPhase) {
      if (snapshot.timedOut) {
        this.warn(`步骤未通过：${getPhaseName(this.lastPhase)}（超时）`);
      } else {
        this.info(`步骤通过：${getPhaseName(this.lastPhase)}`, {
          ...getPoseDetails(snapshot),
          连续确认帧: `${snapshot.requiredFrames}/${snapshot.requiredFrames}`,
        });
      }
    }

    if (snapshot.complete) {
      this.info("最终检测结果：全部活体动作通过");
    } else if (snapshot.timedOut) {
      this.error("最终检测结果：超时未通过");
    } else {
      this.info(`开始检测步骤：${getPhaseName(snapshot.phase)}`);
    }

    this.lastPhase = snapshot.phase;
  }

  logDetectionError(error, consecutiveErrors, maximumErrors) {
    this.warn("单帧检测结果：处理失败，准备重试", {
      连续失败次数: `${consecutiveErrors}/${maximumErrors}`,
      失败原因: error?.message || "未知错误",
    });
  }
}
